import type { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { isPlaceholderAttachment } from './attachment-placeholder';

interface PlaceholderRow {
  role?: string;
  content?: string;
  attachments?: MessageAttachment[];
}

/**
 * A placeholder that the SAME EXCHANGE already answered with a real file is not drawn.
 *
 * A placeholder is a file promised while a design is made; the harness normally fills it in place. When it
 * does not — the agent saved the result into a Space and attached it BY PATH in a new reply, which the
 * fill never took — the promise and the delivery both stay on screen: the finished PDF, and above it a
 * "Rendering your image" game that will never finish (reported 2026-10-09). This covers every row the
 * harness did not fill, including history.
 *
 * "Answered" is deliberately narrow: a LATER assistant-side row carrying a real attachment, before the user
 * next speaks. The oldest placeholders are matched first, one real file each, so a brief that promised more
 * files than arrived keeps its remaining chips. A row left with no attachments and no text is dropped.
 * Pure, so it is tested without the chat.
 */
export function withoutSupersededPlaceholders<T extends PlaceholderRow>(messages: T[]): T[] {
  const drop = new Map<number, number>(); // row index → placeholders to drop from it, oldest first
  let pending: { row: number; count: number }[] = [];
  messages.forEach((message, row) => {
    if (message.role === 'USER') {
      pending = [];
      return;
    }
    const attachments = message.attachments ?? [];
    let delivered = attachments.filter((attachment) => !isPlaceholderAttachment(attachment)).length;
    while (delivered > 0 && pending.length > 0) {
      const oldest = pending[0];
      oldest.count -= 1;
      delivered -= 1;
      drop.set(oldest.row, (drop.get(oldest.row) ?? 0) + 1);
      if (oldest.count === 0) pending.shift();
    }
    const promised = attachments.filter(isPlaceholderAttachment).length;
    if (promised > 0) pending.push({ row, count: promised });
  });
  if (drop.size === 0) return messages;
  return messages.flatMap((message, row) => {
    let toDrop = drop.get(row);
    if (!toDrop) return [message];
    const attachments = (message.attachments ?? []).filter((attachment) => {
      if (toDrop && isPlaceholderAttachment(attachment)) {
        toDrop -= 1;
        return false;
      }
      return true;
    });
    if (attachments.length === 0 && !message.content?.trim()) return [];
    return [{ ...message, attachments }];
  });
}
